/**
 * `AutoActivationBanner` — the transient acknowledgement that a unit's turn just opened **without
 * the player asking for it** (`projects/pre-activation-affordances.md` 4.1).
 *
 * Reads {@link RemoteEncounterController.autoActivation}, which only ever exists on the client
 * whose own gesture caused the fold. A bystanding player's feedback is the turn tracker flipping;
 * this card is deliberately not broadcast. The behavioural contract — transient, non-blocking, no
 * dismissal, auto-activations only — is settled and documented in `./autoActivationNotice.ts`
 * alongside the Risk it exists to answer. **Read that header before changing anything here.**
 *
 * ## What this first pass chose, and what is still the developer's call
 *
 * The behaviour is settled; the *look* is not. The developer picked a direction, not a mock, and
 * named two candidate treatments — animating the turn-tracker chip, or a brief flash on the acting
 * unit. This is a third: a card in the app's established transient-feedback family
 * ({@link RejectionToast}, {@link ReactionWaitBanner}, {@link MoveHoldBanner}), because the failure
 * mode the acknowledgement exists for is *acting through the **wrong mech***, and the only
 * treatment that answers that is one which **says the mech's name** where the player is already
 * looking. A chip pulse in a side panel, or a flash on a token that may be off-camera, tells you
 * that *something* activated. It is routed to his eye as an owed task; if he prefers
 * either of his own two, this component is the only thing that changes — the trigger, the arming
 * and the tests below it are treatment-agnostic.
 *
 * ## Mechanics
 *
 * `role="status"` + `aria-live="polite"`, never `role="alert"`: this is good news about something
 * that worked, and it must not interrupt. **No ✕** — the ruling says nothing to click, so unlike
 * {@link RejectionToast} the only exit is the timer. `key={notice.seq}` replays the enter animation
 * when the same unit auto-activates again (an ELITE/ULTRA's second activation, or next round), and
 * keying the dismiss effect on the `notice` object is what restarts the countdown for it.
 */
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { useEffect, useState } from "react";
import type { AutoActivationNotice } from "./autoActivationNotice";

/**
 * How long the card lingers before it auto-dismisses (ms). Matches {@link RejectionToast}'s own
 * dwell deliberately — one transient rhythm across the board rather than a second tuned number —
 * and it is one of the things flagged for the developer's eye.
 */
const AUTO_DISMISS_MS = 4200;

export function AutoActivationBanner({ notice, name }: { notice: AutoActivationNotice | null; name: string }) {
  // Mirror the live signal into local state so the timer can hide the card while the signal itself
  // stays set (the controller never clears it — a *fresh* auto-activation mints a new object with a
  // bumped seq, which restarts this effect). Same shape as `RejectionToast`'s.
  const [shown, setShown] = useState<AutoActivationNotice | null>(null);
  useEffect(() => {
    if (!notice) {
      setShown(null);
      return;
    }
    setShown(notice);
    const t = setTimeout(() => setShown(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [notice]);

  if (!shown) return null;
  return (
    <div key={shown.seq} className="combat-autoactivate" role="status" aria-live="polite">
      <span className="combat-autoactivate-glyph" aria-hidden="true">
        <Icon icon={faPlay} />
      </span>
      <div className="combat-autoactivate-text">
        <span className="combat-autoactivate-eyebrow">OMNINET ⁄⁄ Turn opened</span>
        <span className="combat-autoactivate-reason">
          <b>{name}</b> is taking its turn.
        </span>
      </div>
    </div>
  );
}
